import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { PurchaseDocumentKind } from '@skylab-shared/dataModel';
import { StringUtils } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';
import { format } from 'fecha';
import { CommunityTransactionForPurchaseQuery, PurchaseCommunityTransactionItem } from '../../community-transaction/queries/community-transaction-for-purchase.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';
import { VatRateForPurchaseQuery } from '../../vat/queries/vat-rate-for-purchase.query-base';
import { VatRateItem, VatRatePLSymbol } from '../../vat/queries/vat-rate-item';

export class PurchaseRegistryCollectionQuery extends Query<PurchaseRegistryModel[]> {
    public constructor(public readonly parameters: PurchaseRegistryParameters) {
        super();
    }
}

export class PurchaseRegistryCollectionQueryHandlerBase implements QueryHandler<PurchaseRegistryCollectionQuery, PurchaseRegistryModel[]> {
    private static Url = TaggedUrl.join('purchaseRegistry', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: PurchaseRegistryCollectionQuery): Promise<PurchaseRegistryModel[]> {
        const { data } = await this.http.get<PurchaseRegistryInternalItemDTO[]>(PurchaseRegistryCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                issueDateFrom: query.parameters.issueDateFrom && format(query.parameters.issueDateFrom, DataFormats.date.webApi),
                issueDateTo: query.parameters.issueDateTo && format(query.parameters.issueDateTo, DataFormats.date.webApi),
                dueDateFrom: query.parameters.dueDateFrom && format(query.parameters.dueDateFrom, DataFormats.date.webApi),
                dueDateTo: query.parameters.dueDateTo && format(query.parameters.dueDateTo, DataFormats.date.webApi),
                saleDateFrom: query.parameters.saleDateFrom && format(query.parameters.saleDateFrom, DataFormats.date.webApi),
                saleDateTo: query.parameters.saleDateTo && format(query.parameters.saleDateTo, DataFormats.date.webApi),
                taxLiabilityDateFrom: query.parameters.taxLiabilityDateFrom && format(query.parameters.taxLiabilityDateFrom, DataFormats.date.webApi),
                taxLiabilityDateTo: query.parameters.taxLiabilityDateTo && format(query.parameters.taxLiabilityDateTo, DataFormats.date.webApi),
            },
            dto: PurchaseRegistryInternalItemDTO
        });

        const paymentMethods = await this.rootContext.getAsync(PaymentMethodQuery);
        const vatRates = await this.rootContext.getAsync(VatRateForPurchaseQuery);
        const transactionKinds = await this.rootContext.getAsync(CommunityTransactionForPurchaseQuery);
        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => {
            return <PurchaseRegistryModel>{
                adjustment: item.adjustment,
                baseCurrencyId: item.baseCurrencyId,
                baseCurrencyGross: item.baseCurrencyGross,
                baseCurrencyNet: item.baseCurrencyNet,
                baseCurrencyVat: item.baseCurrencyVat,
                client: item.clientName,
                clientAddress: item.clientAddress
                    ? StringUtils.join(', ', item.clientAddress.address, item.clientAddress.zipCode, item.clientAddress.city)
                    : null,
                clientCountry: countries.find(elem => elem.id === item.clientAddress?.countryId)?.name,
                clientGroup: item.clientGroup,
                currencyId: item.currencyId,
                documentId: item.documentId,
                documentNumber: item.documentNumber,
                externalDocumentNumber: item.externalDocumentNumber,
                documentKind: item.documentKind,
                gross: item.gross,
                issueDate: item.issueDate,
                saleDate: item.saleDate,
                dueDate: item.dueDate,
                taxLiabilityDate: item.taxLiabilityDate,
                net: item.net,
                tin: item.tin,
                vat: item.vat,
                transactionKind: transactionKinds.find(elem => elem.id === item.transactionKindId),
                paymentMethodName: paymentMethods.find(elem => elem.id === item.paymentMethodId)?.name,
                jpkVatGroups: item.jpkVatGroups?.join(', ') || null,
                vatRates: {
                    baseCurrency: this.parseVatTable(item.baseCurrencyVatTable, vatRates),
                    currency: this.parseVatTable(item.vatTable, vatRates),
                },
                ksefDocumentId: item.ksefDocumentId
            };
        });
    }

    private parseVatTable(vatTable: { gross: number; net: number; vat: number; vatRateId: number; }[] | undefined, vatRates: VatRateItem[]): ReportPurchaseRegistryVatRates {
        const rates: ReportPurchaseRegistryVatRates = {};

        vatTable?.forEach(x => {
            const symbol = vatRates.find(v => v.id === x.vatRateId)?.symbol as VatRatePLSymbol;
            if (symbol === '0%') {
                ({ net: rates.vatRate0Net, gross: rates.vatRate0Gross, vat: rates.vatRate0Vat } = x);
            } else if (symbol === '5%') {
                ({ net: rates.vatRate5Net, gross: rates.vatRate5Gross, vat: rates.vatRate5Vat } = x);
            } else if (symbol === '7%') {
                ({ net: rates.vatRate7Net, gross: rates.vatRate7Gross, vat: rates.vatRate7Vat } = x);
            } else if (symbol === '8%') {
                ({ net: rates.vatRate8Net, gross: rates.vatRate8Gross, vat: rates.vatRate8Vat } = x);
            } else if (symbol === '23%') {
                ({ net: rates.vatRate23Net, gross: rates.vatRate23Gross, vat: rates.vatRate23Vat } = x);
            } else if (symbol === 'zw') {
                ({ net: rates.vatRateZwNet, gross: rates.vatRateZwGross, vat: rates.vatRateZwVat } = x);
            } else if (symbol === 'nieop.') {
                ({ net: rates.vatRateNieopNet, gross: rates.vatRateNieopGross, vat: rates.vatRateNieopVat } = x);
            }
        });

        return rates;
    }
}

export interface PurchaseRegistryParameters {
    convertToPln: boolean;
    issueDateFrom: Date;
    issueDateTo: Date;
    saleDateFrom: Date;
    saleDateTo: Date;
    dueDateFrom: Date;
    dueDateTo: Date;
    taxLiabilityDateFrom: Date;
    taxLiabilityDateTo: Date;
    clientGroupId?: string;
    noClientGroup?: boolean;
    search: string;
    paymentMethodId: number;
}

export interface PurchaseRegistryModel {
    adjustment: boolean;
    baseCurrencyGross: number;
    baseCurrencyId: string;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    client: string;
    clientAddress: string;
    clientCountry: string;
    clientGroup: string;
    currencyId: string;
    documentId: number;
    documentNumber: string;
    externalDocumentNumber: string;
    documentKind: PurchaseDocumentKind;
    gross: number;
    issueDate: Date;
    dueDate: Date;
    saleDate: Date;
    taxLiabilityDate: Date;
    net: number;
    tin: string;
    vat: number;
    transactionKind: PurchaseCommunityTransactionItem;
    paymentMethodName?: string;
    jpkVatGroups: string;
    vatRates: {
        currency: ReportPurchaseRegistryVatRates,
        baseCurrency: ReportPurchaseRegistryVatRates
    };
    ksefDocumentId: string;
}

export interface ReportPurchaseRegistryVatRates {
    vatRate7Net?: number;
    vatRate7Gross?: number;
    vatRate7Vat?: number;

    vatRateZwNet?: number;
    vatRateZwGross?: number;
    vatRateZwVat?: number;

    vatRateNieopNet?: number;
    vatRateNieopGross?: number;
    vatRateNieopVat?: number;

    vatRate0Net?: number;
    vatRate0Gross?: number;
    vatRate0Vat?: number;

    vatRate5Net?: number;
    vatRate5Gross?: number;
    vatRate5Vat?: number;

    vatRate8Net?: number;
    vatRate8Gross?: number;
    vatRate8Vat?: number;

    vatRate23Net?: number;
    vatRate23Gross?: number;
    vatRate23Vat?: number;
}

class PurchaseRegistryInternalItemDTO {
    public readonly clientAddress!: {
        address: string;
        city: string;
        countryId: number;
        name: string;
        zipCode: string;
    };
    public readonly vatTable!: {
        gross: number;
        net: number;
        vat: number;
        vatRateId: number;
    }[];
    public readonly baseCurrencyVatTable!: {
        gross: number;
        net: number;
        vat: number;
        vatRateId: number;
    }[];
    public readonly clientGroup!: string;
    public readonly clientName!: string;
    public readonly currencyId!: string;
    public readonly documentId!: number;
    public readonly documentNumber!: string;
    public readonly externalDocumentNumber!: string;
    public readonly documentKind!: PurchaseDocumentKind;
    public readonly gross!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly jpkVatGroups!: string[];
    public readonly net!: number;
    public readonly paymentMethodId!: number;
    public readonly precision!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly saleDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxLiabilityDate!: Date;
    public readonly tin!: string;
    public readonly transactionKindId!: number;
    public readonly vat!: number;
    public readonly adjustment!: boolean;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyId!: string;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyVat!: number;
    public readonly ksefDocumentId!: string;
}
